// src/talent/components/MessagesList.jsx
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Select,
  MenuItem,
  Button,
  Checkbox,
} from "@mui/material";
import MessageCard from "./MessageCard";
import AppLoader from "./components/common/AppLoader";


const MessagesList = ({
  messages = [],
  loading,
  onReply,
  onArchive,
  onInbox,
  onDelete,
  tabIndex,
  onSortChange,
  sortOrder,
  onSelectionChange, // NEW: lets the parent know if anything is selected (used to pause polling)
}) => {
  const [selectedMessages, setSelectedMessages] = useState([]);

  // Clear selection whenever messages or tab changes
  useEffect(() => {
    setSelectedMessages([]);
  }, [messages, tabIndex]);

  // NEW: tell the parent whenever the selection count changes
  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedMessages.length > 0);
  }, [selectedMessages, onSelectionChange]);

  // Use AppLoader instead of hardcoded CircularProgress
  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <AppLoader message="Loading messages..." />
      </Box>
    );

  // --- Selection helpers ---
  const getKey = (message) => message.messageId ?? message.originalMessageId;

  const handleSelectMessage = (message) => {
    const key = getKey(message);

    setSelectedMessages((prevSelected) => {
      const isSelected = prevSelected.some((m) => getKey(m) === key);
      if (isSelected) {
        return prevSelected.filter((m) => getKey(m) !== key);
      }
      return [
        ...prevSelected,
        {
          messageId: message.messageId,
          originalMessageId: message.originalMessageId,
        },
      ];
    });
  };

  // --- Select all toggle ---
  const allSelected =
    messages.length > 0 && selectedMessages.length === messages.length;
  const someSelected =
    selectedMessages.length > 0 && selectedMessages.length < messages.length;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(
        messages.map((message) => ({
          messageId: message.messageId,
          originalMessageId: message.originalMessageId,
        }))
      );
    }
  };

  // --- Bulk Actions ---
  const handleBulkArchive = () => {
    if (selectedMessages.length > 0) {
      const ids = selectedMessages.map((msg) => msg.originalMessageId);
      onArchive(ids);
      setSelectedMessages([]);
    }
  };

  const handleBulkInbox = () => {
    if (selectedMessages.length > 0) {
      const ids = selectedMessages.map((msg) => msg.originalMessageId);
      onInbox(ids);
      setSelectedMessages([]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedMessages.length > 0) {
      const ids = selectedMessages.map((msg) => msg.originalMessageId);
      onDelete(ids);
      setSelectedMessages([]);
    }
  };

  // --- Render ---
  return (
    <Box>
      {/* Bulk Actions & Sorting */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginLeft: "17px" }}>
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected}
            onChange={handleSelectAll}
            disabled={messages.length === 0}
          />

          {selectedMessages.length > 0 ? (
            <Box>
              {onArchive && (
                <Button onClick={handleBulkArchive}>
                  Archive Selected ({selectedMessages.length})
                </Button>
              )}

              {onInbox && (
                <Button onClick={handleBulkInbox}>
                  Inbox Selected ({selectedMessages.length})
                </Button>
              )}

              {onDelete && (
                <Button onClick={handleBulkDelete}>
                  Delete Selected ({selectedMessages.length})
                </Button>
              )}
            </Box>
          ) : (
            <Typography sx={{ fontWeight: 500 }}>Select all</Typography>
          )}
        </Box>

        {/* Sorting Dropdown */}
        <Select
          value={sortOrder}
          onChange={(e) => onSortChange(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="Latest">Newest First</MenuItem>
          <MenuItem value="Unread">Unread First</MenuItem>
        </Select>
      </Box>

      {/* Display Messages */}
      {messages.length > 0 ? (
        messages.map((message) => (
          <MessageCard
            key={message.messageId}
            message={message}
            onReply={onReply}
            onArchive={
              onArchive
                ? () => onArchive([message.originalMessageId])
                : undefined
            }
            onInbox={
              onInbox
                ? () => onInbox([message.originalMessageId])
                : undefined
            }
            isSelected={selectedMessages.some(
              (m) => getKey(m) === getKey(message)
            )}
            onSelect={() => handleSelectMessage(message)}
            tabIndex={tabIndex}
            onDelete={() => onDelete([message.originalMessageId])}
          />
        ))
      ) : (
        <Typography sx={{ mt: 2, textAlign: "center", color: "gray" }}>
          No messages available.
        </Typography>
      )}
    </Box>
  );
};

export default MessagesList;